import * as Utils from "./Utils";

const storageChangeListeners: StorageChangeListener[] = [];

export let memoryStore: Record<string, any> = {};

const MOCK_STORAGE: Storage = {
	getItem: () => undefined,
} as any;

export interface StorageOptions {
	session?: boolean;
	expiry?: number;
}

const defaultOptions: StorageOptions = {};

export interface StorageKey<T> {
	id: string;
	defaultValue: T | undefined;
	options: StorageOptions;
}

export const createStorageKey = <T>(
	id: string,
	defaultValue?: T,
	options?: StorageOptions,
): StorageKey<T> => {
	return {
		id,
		defaultValue: defaultValue || undefined,
		options: options || defaultOptions,
	};
};

const getStorage = <T>(
	key: StorageKey<T>,
	options?: StorageOptions,
): Storage => {
	const session =
		typeof options?.session === "boolean"
			? options.session
			: key.options?.session;
	const storage = session ? window.sessionStorage : window.localStorage;
	return storage || MOCK_STORAGE;
};

export const toggle = (
	key: StorageKey<boolean>,
	value?: boolean,
	options?: StorageOptions,
) => {
	let newValue = Boolean(value);
	if (value === undefined) {
		newValue = !get(key);
	}
	set(key, newValue, options);
};

export const set = <T>(
	key: StorageKey<T>,
	value: T,
	options?: StorageOptions,
) => {
	if (value === undefined) {
		return remove(key);
	}

	const storage = getStorage(key);
	const currentEntry = entry(key);
	let createTime = Date.now();
	try {
		if (currentEntry) {
			createTime = currentEntry.createTime;
		}
		const updateTime = Date.now();

		const computedOptions = { ...key.options, ...options };
		const expiryTime = computedOptions.expiry
			? updateTime + computedOptions.expiry
			: undefined;
		const entry = {
			value: value,
			updateTime,
			createTime,
			expiryTime,
		};
		memoryStore[key.id] = entry;
		for (const listener of storageChangeListeners) {
			listener(key, value);
		}
		// defer actual set, faster
		Utils.nextFrame(() => storage.setItem(key.id, JSON.stringify(entry)));
	} catch (error) {
		console.error(error);
	}
};

export interface StorageEntry<T> {
	expiryTime: number;
	createTime: number;
	value: T;
}

export const get = <T>(key: StorageKey<T>, defaultValue?: T): T | undefined => {
	const currentEntry = entry(key);
	try {
		if (currentEntry) {
			return currentEntry.value;
		}
	} catch (error) {
		console.error(error);
	}
	return defaultValue === undefined ? key.defaultValue : defaultValue;
};

export const entry = <T>(key: StorageKey<T>): StorageEntry<T> | undefined => {
	let entry = null;

	if (memoryStore[key.id] !== undefined) {
		entry = memoryStore[key.id];
	} else {
		const storage = getStorage(key);
		const item = storage.getItem(key.id);
		if (!item) {
			return undefined;
		}
		try {
			entry = item ? JSON.parse(item) : undefined;
		} catch (error) {
			console.error(error);
		}
	}

	if (entry) {
		if (entry.expiryTime > 0 && Date.now() > entry.expiryTime) {
			remove(key);
			return undefined;
		}
	}
	memoryStore[key.id] = entry;
	return entry;
};

export const clear = (options?: StorageOptions): Record<string, any> => {
	const storage = getStorage({} as any, options);
	const data: Record<string, any> = {};
	Object.keys(storage).forEach((key) => {
		data[key] = JSON.parse(storage.getItem(key) as any);
	});
	console.warn(`Cleared ${Object.keys(data).length} storage keys`);

	storage.clear();
	memoryStore = {};
	return data;
};

export const clearRegex = (
	regex: RegExp[],
	options?: StorageOptions,
): Record<string, any> => {
	const storage = getStorage({} as any, options);
	const data: Record<string, any> = {};
	const regexs = Array.isArray(regex) ? regex : [regex];
	Object.keys(storage).forEach((key) => {
		if (regexs.every((regex) => regex.test(key))) {
			data[key] = JSON.parse(storage.getItem(key) as any);
		}
	});
	Object.keys(data).forEach((key) => {
		storage.removeItem(key);
		delete memoryStore[key];
	});
	console.warn(`Cleared ${Object.keys(data).length} storage keys`);
	return data;
};

export const has = <T>(key: StorageKey<T>): boolean => {
	return Boolean(entry(key));
};

export const remove = <T>(key: StorageKey<T>): void => {
	const storage = getStorage(key);
	memoryStore[key.id] = null;

	// defer actual remove, faster
	Utils.nextFrame(() => {
		storage.removeItem(key.id);
		for (const listener of storageChangeListeners) {
			listener(key, undefined);
		}
	});
};

export type StorageChangeListener<T = any> = (
	key: StorageKey<T>,
	value: T | undefined,
) => void;

export const addListener = <T>(
	listener: StorageChangeListener<T>,
): StorageChangeListener<T> => {
	storageChangeListeners.push(listener);
	return listener;
};

export const removeListener = <T>(listener: StorageChangeListener<T>) => {
	storageChangeListeners.splice(storageChangeListeners.indexOf(listener), 1);
};
